import {
  Autocomplete,
  AutocompleteChangeReason,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { SyntheticEvent, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { SmartyAddress, SmartyResponse } from './types';
import debounce from 'lodash/debounce';

interface Props {
  onChange: (field: string, value: any) => void;
  data: Record<string, any>;
  errors?: { [key: string]: string };
  setAddress1: (e: string) => void;
  inputValue: string | undefined;
  onFocus?: () => void;
  onBlur?: () => void;
  setFullAddress: (fullAddress: any) => void;
  errorKey: string;
  errorCityKey: string;
  errorStateKey: string;
  errorZipKey: string;
}

const AddressDropdown = (props: Props) => {
  const [addressQuery, setAddressQuery] = useState('');
  const [addressList, setAddressList] = useState<SmartyAddress[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<any>();
  const [inputValue, setInputValue] = useState('');

  const SMARTY_API_URL = 'https://us-autocomplete-pro.api.smarty.com/lookup';
  const AUTH_KEY = '228723848763357206';

  useEffect(() => {
    if (addressQuery?.trim()) {
      axios
        .get(SMARTY_API_URL, {
          params: {
            key: AUTH_KEY,
            search: addressQuery,
            prefer_geolocation: 'none',
          },
        })
        .then((response) => {
          setAddressList(response.data.suggestions || []);
        })
        .catch((error) => {
          console.error('Error fetching address suggestions', error);
        });
    } else {
      setAddressList([]);
    }
  }, [addressQuery]);

  const handleOptionChange = (option: SmartyAddress | null) => {
    if (!option) return;
    setSelectedAddress({
      address: option.street_line,
      address2: option.secondary || '',
      city: option.city || '',
      state: option.state || '',
      zip: option.zipcode || '',
    });
    setInputValue(option.street_line);
    props.setAddress1(option.street_line);
    props.setFullAddress({
      address: option.street_line,
      address2: option.secondary || '',
      city: option.city || '',
      state: option.state || '',
      zip: option.zipcode || '',
    });
  };

  const handleAddressOptionChange = (
    e: SyntheticEvent<Element, Event>,
    option: SmartyAddress | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption') {
      handleOptionChange(option);
    } else if (reason === 'clear') {
      clearAddress1AndOptions();
    }
  };

  const handleAddress1Change = async (searchText: string) => {
    setInputValue(searchText);
    props.setAddress1(searchText);
  };

  const clearAddress1AndOptions = () => {
    props.setAddress1('');
    setInputValue('');
    setAddressList([]);
  };

  return (
    <Stack marginTop={3}>
      <Autocomplete
        freeSolo
        getOptionLabel={(option: any) =>
          `${option?.street_line}, ${option?.city}, ${option?.state} ${option?.zipcode}`
        }
        onInputChange={(event, newInputValue) => {
          setAddressQuery(newInputValue);
        }}
        sx={{ backgroundColor: '#fff' }}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.street_line + option.zipcode}>
              <div style={{ width: '100%' }}>
                <div>
                  <Typography
                    sx={{ display: 'inline-block' }}
                    variant='subtitle2'
                  >
                    {option.street_line} {option.secondary} {option.city}{' '}
                    {option.state} {option.zipcode}
                  </Typography>
                </div>
              </div>
            </li>
          );
        }}
        options={addressList}
        inputValue={props.inputValue !== undefined ? props.inputValue : inputValue}
        onChange={(e, option: any, reason) =>
          handleAddressOptionChange(e, option, reason)
        }
        clearOnEscape
        renderInput={(params) => (
          <TextField
            error={Boolean(props.errors?.[props.errorKey])}
            {...params}
            inputProps={{
              ...params.inputProps,
              autocomplete: 'new-password',
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='end'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            fullWidth
            label='Address'
            placeholder='Address Search'
            onChange={(e) => handleAddress1Change(e.target.value)}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            required
          />
        )}
      />
      <Stack marginTop={2}>
        <TextField
          label='Apartment, suite, etc. (optional)'
          variant='outlined'
          fullWidth
          value={selectedAddress?.address2 ?? props.data.address2 ?? ''}
          onChange={(e) => {
            setSelectedAddress({
              ...selectedAddress,
              address2: e.target.value,
            });
            props.onChange('address2', e.target.value);
          }}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
        />
      </Stack>
      <Stack
        direction='row'
        justifyContent='space-between'
        spacing={3}
        marginTop={2}
      >
        <TextField
          label='City'
          variant='outlined'
          fullWidth
          value={selectedAddress?.city ?? props.data.city ?? ''}
          onChange={(e) => {
            setSelectedAddress({ ...selectedAddress, city: e.target.value });
            props.onChange('city', e.target.value);
          }}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
          error={Boolean(props.errors?.[props.errorCityKey])}
          required
        />
        <TextField
          label='State'
          variant='outlined'
          fullWidth
          value={selectedAddress?.state ?? props.data.state ?? ''}
          onChange={(e) => {
            setSelectedAddress({ ...selectedAddress, state: e.target.value });
            props.onChange('state', e.target.value);
          }}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
          error={Boolean(props.errors?.[props.errorStateKey])}
          required
        />
        <TextField
          label='ZIP Code'
          variant='outlined'
          fullWidth
          value={selectedAddress?.zip ?? props.data.zip ?? ''}
          onChange={(e) => {
            setSelectedAddress({
              ...selectedAddress,
              zip: e.target.value,
            });
            props.onChange('zip', e.target.value);
          }}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
          error={Boolean(props.errors?.[props.errorZipKey])}
          required
        />
      </Stack>
    </Stack>
  );
};


export default AddressDropdown;
